import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, ArrowUpRight, LayoutGrid } from "lucide-react";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import { NAV_LINKS } from "../data/content";
import { useAuth } from "../context/AuthContext";
import {
  Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription,
} from "./ui/sheet";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const loggedIn = !!user;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-300 ${
        scrolled
          ? "border-b border-[color:var(--hairline)] bg-[color:var(--surface-glass)] backdrop-blur-xl"
          : "border-b border-transparent"
      }`}
    >
      <nav className="container-hub flex h-16 items-center justify-between md:h-[74px]">
        <Logo />

        <div className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((link) => {
            const active = pathname === link.to;
            return (
              <Link key={link.to} to={link.to} data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                  active ? "text-[color:var(--ink)]" : "text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]"}`}>
                {link.label}
                {active && <span className="brand-bar absolute inset-x-4 -bottom-0.5 h-0.5 rounded-full" />}
              </Link>
            );
          })}
        </div>

        <div className="hidden items-center gap-2.5 md:flex">
          <ThemeToggle />
          {loggedIn ? (
            <button onClick={() => navigate("/app")} data-testid="nav-workspace"
              className="group inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">
              <LayoutGrid className="h-4 w-4" /> Workspace
            </button>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login"
                className="rounded-full px-4 py-2 text-sm font-medium text-[color:var(--ink-soft)] transition-colors duration-200 hover:text-[color:var(--ink)]">
                Login
              </Link>
              <Link to="/join" data-testid="nav-join-hub"
                className="group inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">
                Join Hub
                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <ThemeToggle />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-trigger" aria-label="Open menu"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)]">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86%] max-w-sm border-l border-[color:var(--hairline)] bg-[color:var(--surface)]">
              <SheetTitle className="sr-only">StUpEco Hub navigation</SheetTitle>
              <SheetDescription className="sr-only">Primary navigation links and account actions</SheetDescription>
              <div className="mt-2 mb-8"><Logo /></div>
              <div className="flex flex-col gap-1">
                {NAV_LINKS.map((link) => (
                  <SheetClose asChild key={link.to}>
                    <Link to={link.to} data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                      className="font-display rounded-xl px-3 py-3 text-2xl font-medium tracking-tight text-[color:var(--ink)] transition-colors hover:bg-[color:var(--soft)]">
                      {link.label}
                    </Link>
                  </SheetClose>
                ))}
              </div>
              <div className="mt-8 flex flex-col gap-3">
                {loggedIn ? (
                  <SheetClose asChild>
                    <Link to="/app" data-testid="mobile-nav-workspace" className="rounded-full bg-[color:var(--invert-bg)] px-5 py-3 text-center text-sm font-semibold text-white">Workspace</Link>
                  </SheetClose>
                ) : (
                  <>
                    <SheetClose asChild>
                      <Link to="/login" data-testid="mobile-nav-login" className="rounded-full border border-[color:var(--hairline)] px-5 py-3 text-center text-sm font-semibold text-[color:var(--ink)]">Login</Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/join" data-testid="mobile-nav-join-hub" className="rounded-full bg-[color:var(--invert-bg)] px-5 py-3 text-center text-sm font-semibold text-white">Join Hub</Link>
                    </SheetClose>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
